import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "../config";
import { THEMES, customToVars, applyThemeVars, setBodyThemeAttr } from "../themes";

const ThemeContext = createContext({
  name: "default",
  custom: null,
  setTheme: () => {},
  setCustomTheme: () => {},
});

export const ThemeProvider = ({ children }) => {
  const [name, setName] = useState(() => {
    try {
      // The "default" light theme was removed because it produced unreadable
      // contrast combos in many SaaS components. Anyone persisted on it gets
      // transparently migrated to cyberpunk (dark purple) on next page load.
      const stored = localStorage.getItem("site_theme");
      if (!stored || stored === "default") return "cyberpunk";
      return stored;
    } catch {
      return "cyberpunk";
    }
  });
  const [custom, setCustom] = useState(() => {
    try {
      const raw = localStorage.getItem("site_theme_custom");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  // Apply theme to :root whenever it changes
  useEffect(() => {
    if (name === "custom" && custom) {
      applyThemeVars(customToVars(custom));
    } else {
      const t = THEMES[name] || THEMES.default;
      applyThemeVars(t.vars);
    }
    setBodyThemeAttr(name);
  }, [name, custom]);

  // Fetch from backend on mount (source of truth)
  useEffect(() => {
    axios
      .get(`${API}/theme/active`)
      .then((r) => {
        const d = r.data || {};
        // Backward-compat: server may still return "default" for accounts that
        // haven't touched the selector since we removed the light theme. Coerce
        // it to cyberpunk so the UI stays readable.
        const serverName = d.name === "default" ? "cyberpunk" : d.name;
        if (serverName && serverName !== name) {
          setName(serverName);
          try { localStorage.setItem("site_theme", serverName); } catch { /* noop */ }
        }
        if (d.custom) {
          setCustom(d.custom);
          try { localStorage.setItem("site_theme_custom", JSON.stringify(d.custom)); } catch { /* noop */ }
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setTheme = useCallback(async (newName, newCustom = null) => {
    setName(newName);
    if (newCustom) setCustom(newCustom);
    try {
      localStorage.setItem("site_theme", newName);
      if (newCustom) localStorage.setItem("site_theme_custom", JSON.stringify(newCustom));
    } catch {}
    try {
      await axios.post(`${API}/theme/set`, {
        name: newName,
        custom: newName === "custom" ? newCustom : null,
      });
    } catch (e) {
      console.error("theme persist error:", e);
    }
  }, []);

  return (
    <ThemeContext.Provider value={{ name, custom, setTheme, setCustomTheme: setCustom }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
